import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, MapPin, Phone } from "lucide-react";
import weststarLogo from "../assets/weststar-logo.webp";
import poweredByManna from "../assets/powered-by-manna.png";
import {
  fmtClock, fmtClockRange, fmtFullDate, fmtMonthDay, fmtWeekday,
  messageForReason, t as tr,
} from "../locales/patientSchedulingI18n";

// Patient availability page — Contract §11, scheduling Commit 3.
//
// PUBLIC and ISOLATED: mounted as a sibling of /login, OUTSIDE the `/`
// Layout route, so no sidebar, no nav, no staff chrome, and nothing here
// touches ProtectedRoute. It calls only the public endpoints and never
// reads auth state.
//
// PHI-MINIMAL: the only patient datum this page ever renders is a FIRST
// name, plus the clinic label and the WestStar phone number. There is no
// last name, birth date, phone, condition or case detail in the payload it
// receives, by design (see `_page_identity` on the server).
//
// Brand is WESTSTAR — the patient knows WestStar, not Manna. The logo and
// the "powered by Manna" mark are the client's own supplied assets,
// bundled LOCALLY (src/assets) so this page still makes no external
// request; nothing here is fabricated or fetched from a CDN.
//
// BILINGUAL (2026-06): every user-facing word comes from the locale layer
// (`src/locales/patientScheduling.{en,es}.js`) through `tr(lang, key)` —
// no user-facing literal lives in this file, which is pinned at grep
// level. The language is CHOSEN, never inferred: the first screen asks,
// the browser locale may pre-highlight but never auto-skips, and the
// header toggle re-renders the current step in place without losing a
// single entered window. The choice is written to the patient row so
// future communications render in-language.


// ── Design tokens (approved prototype source; WestStar palette) ──────────
// Kept LOCAL to this file on purpose: the staff bundle must not gain a
// shared dependency for a patient-facing page, and this page must stay
// self-contained. No new packages, no Tailwind config changes — colour and
// type ride inline styles layered over the existing layout classes, so
// every handler, testid and aria attribute is untouched.
const C = {
  page: "#fcfaf5",          // warm off-white
  surface: "#ffffff",
  panel: "#f8f9f5",         // subtle secondary panel
  text: "#17382a",          // deep green-black
  muted: "#5c6c63",         // secondary text (4.5:1+ on #fcfaf5)
  accent: "#235b45",        // deep green: selection, primary, brand
  accentSoft: "#eef4ef",    // selected chip tint
  border: "#d3dfcb",        // soft green-gray
  error: "#b3261e",         // accessible red for inline validation
  focus: "#2458bd",
};
const FONT = "Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const S = {
  h1: { fontSize: 28, lineHeight: 1.2, letterSpacing: "-0.75px", color: C.text, fontWeight: 700, outlineColor: C.focus },
  body: { fontSize: 17, lineHeight: 1.5, color: C.muted },
  dayHeading: { fontSize: 19, fontWeight: 600, color: C.text, letterSpacing: "-0.2px" },
  dayDate: { fontSize: 15, color: C.muted },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 18, padding: 15 },
  chipOff: { background: C.surface, borderColor: C.border, color: C.text, borderRadius: 13, borderWidth: 1.5, fontSize: 16 },
  chipOn: { background: C.accentSoft, borderColor: C.accent, color: C.text, borderRadius: 13, borderWidth: 2, fontSize: 16, fontWeight: 600 },
  primary: { background: C.accent, color: "#fff", borderRadius: 14, fontSize: 17, fontWeight: 600, minHeight: 56 },
  secondary: { background: C.surface, color: C.accent, border: `1.5px solid ${C.border}`, borderRadius: 13, fontSize: 15, fontWeight: 600 },
  eyebrow: { color: C.accent, fontSize: 13, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase" },
  pill: { background: C.panel, border: `1px solid ${C.border}`, color: C.muted, borderRadius: 999, fontSize: 14, padding: "6px 12px" },
  helper: { fontSize: 14, color: C.muted },
  errorText: { fontSize: 14, color: C.error, fontWeight: 600 },
  langOff: { background: C.surface, borderColor: C.border, color: C.muted, fontSize: 14, fontWeight: 600 },
  langOn: { background: C.accentSoft, borderColor: C.accent, color: C.accent, fontSize: 14, fontWeight: 700 },
};
// One visible focus ring for everything interactive (3px, 2px offset).
const FOCUS_CSS = `
  [data-patient-page] *:focus-visible { outline: 3px solid ${C.focus}; outline-offset: 2px; }
  [data-patient-page] select { color: ${C.text}; background: ${C.surface}; font-size: 16px; }
`;

const API = process.env.REACT_APP_BACKEND_URL;
// The URL SLUG (not the service's purpose constant — the router bridges the
// two via SLUG_TO_PURPOSE; passing the constant here is the 2026-09-13 bug).
const SLUG = "initial-eval";
const PHONE = "888-786-2888";

const MAX_WINDOWS = 5;
const VISIBLE_DAYS = 14;            // two weeks visible
const HORIZON_DAYS = 42;            // "Later dates →" pages up to 6 weeks
const PAGE_DAYS = 14;

// Chip WINDOWS only — the label and the "8:00 AM – 12:00 PM" hint are
// rendered from the locale layer and Intl, never stored as English here.
const CHIPS = [
  { key: "morning", start: "08:00", end: "12:00" },
  { key: "afternoon", start: "12:00", end: "15:00" },
  { key: "late_afternoon", start: "15:00", end: "18:00" },
];

const STEP_TIMES = (() => {
  const out = [];
  for (let h = 7; h <= 19; h += 1) {
    for (const m of ["00", "30"]) out.push(`${String(h).padStart(2, "0")}:${m}`);
  }
  return out;
})();

// The explicit choice is remembered for THIS browsing session only, keyed
// by the link's prefix. A fresh visit always gets the choice screen again
// (the rule is against INFERRING a language, not against remembering an
// answer the patient just gave — a mid-flow reload must not dump them back
// to the start). Nothing here is a login and nothing survives the session.
const LANG_SESSION_PREFIX = "ws-patient-lang:";

function sessionKey(token) {
  return `${LANG_SESSION_PREFIX}${String(token || "").slice(0, 8)}`;
}

function rememberedLang(token) {
  try {
    const v = window.sessionStorage.getItem(sessionKey(token));
    return v === "en" || v === "es" ? v : null;
  } catch (err) {
    return null;                      // private mode / storage disabled
  }
}

function rememberLang(token, lang) {
  try {
    window.sessionStorage.setItem(sessionKey(token), lang);
  } catch (err) { /* nothing to do — the choice still applies this render */ }
}

// Pre-highlight only. This never selects, never advances, never persists.
function browserPreference() {
  try {
    const tags = [navigator.language, ...(navigator.languages || [])];
    return tags.some((tag) => String(tag || "").toLowerCase().startsWith("es"))
      ? "es" : "en";
  } catch (err) {
    return "en";
  }
}

// Dates are built from LOCAL calendar parts and sent as plain YYYY-MM-DD, so
// a patient in another timezone cannot shift the day. Times are labelled
// Pacific because that is the clinic's clock, and never converted.
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayList(offsetDays, count) {
  const out = [];
  const base = new Date();
  base.setHours(12, 0, 0, 0);
  for (let i = 1 + offsetDays; i < 1 + offsetDays + count; i += 1) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    if (d.getDay() === 0) continue;            // clinics are closed Sundays
    out.push(d);
  }
  return out;
}

function minutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

export default function PatientScheduling() {
  const { purpose } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");

  // loading|language|flow|clinic|review|done|unavailable
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [picked, setPicked] = useState([]);        // [{date,start,end,chip}]
  const [exactFor, setExactFor] = useState(null);
  const [exact, setExact] = useState({ from: "09:00", until: "11:00" });
  const [exactError, setExactError] = useState("");
  const [pageOffset, setPageOffset] = useState(0);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");
  const [clinics, setClinics] = useState([]);
  const [chosen, setChosen] = useState("");
  const [lang, setLang] = useState(() => rememberedLang(token));
  const [failNote, setFailNote] = useState("");
  // Telehealth checkbox (2026-10-07): which clinics show Geoff's box (from
  // /clinics) and the answer, checked by default as he asked.
  const [telehealthCodes, setTelehealthCodes] = useState([]);
  const [telehealthOk, setTelehealthOk] = useState(true);
  const headingRef = useRef(null);
  // ── Wave 3 (2026-10-06): booking a real open time at a WestStar clinic ──
  // Only when the server answers self_book: true for the chosen clinic. Any
  // other answer, an error, or "none of these work" keeps the windows flow.
  const [clinicsTried, setClinicsTried] = useState(false);
  const [probeDone, setProbeDone] = useState(false);
  const [windowsOnly, setWindowsOnly] = useState(false);
  const [book, setBook] = useState(null);           // the /slots answer
  const [bookDay, setBookDay] = useState("");
  const [bookHalf, setBookHalf] = useState("all");  // all | am | pm
  const [bookPick, setBookPick] = useState(null);   // {date, start}
  const [bookError, setBookError] = useState("");
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);       // {date, start, clinic}
  const [pageStarts, setPageStarts] = useState([]); // earlier pages, for "back"

  // The render language. `lang` is NULL until the patient chooses, which is
  // what makes the fail-closed screen bilingual for a choiceless arrival.
  const L = lang || "en";
  const T = useCallback((key, vars) => tr(L, key, vars), [L]);

  // Screen readers pronounce Spanish only when told which language it is.
  useEffect(() => {
    if (lang) document.documentElement.lang = lang;
  }, [lang]);

  const resolveToken = useCallback(async () => {
    try {
      const res = await fetch(
        `${API}/api/public/scheduling-link/${purpose || SLUG}?t=${encodeURIComponent(token || "")}`,
      );
      const data = await res.json();
      if (data && data.valid) {
        setInfo(data);
        // An explicit choice already made THIS session resumes the flow; a
        // fresh visit always sees the choice screen.
        setState(rememberedLang(token) ? "flow" : "language");
      } else {
        setFailNote(data ? String(data.reason || "") : "");
        setState("unavailable");
      }
    } catch (err) {
      setFailNote("");
      setState("unavailable");
    }
  }, [purpose, token]);

  useEffect(() => { resolveToken(); }, [resolveToken]);

  // The clinic choice list — eligibility-filtered and ranked server-side.
  // Language changes RENDERING only: this call carries no language and the
  // eligibility rules are identical in both.
  const loadClinics = useCallback(async () => {
    try {
      const res = await fetch(
        `${API}/api/public/scheduling-link/${purpose || SLUG}/clinics?t=${encodeURIComponent(token || "")}`,
      );
      const data = await res.json();
      if (data && data.valid && Array.isArray(data.clinics)) {
        setClinics(data.clinics);
        setTelehealthCodes(Array.isArray(data.telehealth_codes) ? data.telehealth_codes : []);
        const preselect = data.clinics.find((c) => c.is_assigned) || data.clinics[0];
        // Wave 3: keep a clinic the patient already chose (self-booking can
        // hand back to this step with a different clinic selected).
        if (preselect) setChosen((cur) => cur || preselect.location_code);
      }
    } catch (err) {
      setClinics([]);
    } finally {
      setClinicsTried(true);
    }
  }, [purpose, token]);

  useEffect(() => { if (state === "flow") loadClinics(); }, [state, loadClinics]);

  useEffect(() => {
    if (headingRef.current) headingRef.current.focus();
  }, [state]);

  // The preference write — earliest capture, so an abandoner's language
  // still improves their next text. Fire-and-forget: a failed write must
  // never block the patient's actual task.
  const storePreference = useCallback((next) => {
    try {
      fetch(
        `${API}/api/public/scheduling-link/${purpose || SLUG}/language?t=${encodeURIComponent(token || "")}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ language: next }),
        },
      ).catch(() => {});
    } catch (err) { /* the page keeps working without the write */ }
  }, [purpose, token]);

  // ONE path for the first choice and every later toggle: apply, remember,
  // store, announce. Entered windows and the clinic selection are untouched
  // — switching re-renders the current step in place.
  const applyLanguage = useCallback((next, { advance = false } = {}) => {
    setLang(next);
    rememberLang(token, next);
    storePreference(next);
    setStatus(tr(next, "status.languageSet"));
    if (advance) setState("flow");
  }, [token, storePreference]);

  const days = useMemo(() => dayList(pageOffset, VISIBLE_DAYS), [pageOffset]);
  const canPageMore = pageOffset + PAGE_DAYS < HORIZON_DAYS;
  const full = picked.length >= MAX_WINDOWS;

  const isPicked = (date, start, end) =>
    picked.some((p) => p.date === date && p.start === start && p.end === end);

  const toggle = (date, chip) => {
    const label = T(`chips.${chip.key}`);
    const existing = isPicked(date, chip.start, chip.end);
    if (existing) {
      setPicked((prev) => prev.filter(
        (p) => !(p.date === date && p.start === chip.start && p.end === chip.end)));
      setStatus(T("status.removed", {
        window: label, count: picked.length - 1, max: MAX_WINDOWS,
      }));
      return;
    }
    if (full) {
      setStatus(T("errors.maxWindowsRemove", { max: MAX_WINDOWS }));
      return;
    }
    setPicked((prev) => [...prev, { date, start: chip.start, end: chip.end, chip: label }]);
    setStatus(T("status.added", {
      window: label, count: picked.length + 1, max: MAX_WINDOWS,
    }));
  };

  const addExact = (date) => {
    const span = minutes(exact.until) - minutes(exact.from);
    if (span < 45) {
      setExactError(T("errors.minWindow", { minutes: 45 }));
      return;
    }
    if (span <= 0) {
      setExactError(T("errors.endAfterStart"));
      return;
    }
    if (full) {
      setExactError(T("errors.maxWindows", { max: MAX_WINDOWS }));
      return;
    }
    setExactError("");
    setPicked((prev) => [...prev, {
      date, start: exact.from, end: exact.until,
      chip: fmtClockRange(L, exact.from, exact.until),
    }]);
    setExactFor(null);
    setStatus(T("status.exactAdded", {
      count: picked.length + 1, max: MAX_WINDOWS,
    }));
  };

  const showTelehealth = telehealthCodes.includes(chosen);

  const send = async () => {
    setSending(true);
    try {
      const res = await fetch(
        `${API}/api/public/scheduling-link/${purpose || SLUG}/submit?t=${encodeURIComponent(token || "")}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            windows: picked.map((p) => ({ date: p.date, start: p.start, end: p.end })),
            location_code: chosen || undefined,
            // Sent only when the box was on screen; the server re-checks.
            ...(showTelehealth ? { telehealth_ok: telehealthOk } : {}),
          }),
        },
      );
      const data = await res.json();
      if (data && data.received) {
        setState("done");
      } else {
        // expired mid-session, already submitted, or refused: one honest page
        setFailNote(data ? String(data.reason || "") : "");
        setState("unavailable");
      }
    } catch (err) {
      setStatus(T("errors.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  // The compact in-header switcher, on every step after the choice. It
  // re-renders in place: no state is reset, so windows and the chosen
  // clinic survive a mid-flow switch.
  // ── Wave 3: self-booking actions ──────────────────────────────────────────
  const loadSlots = useCallback(async (code, start) => {
    try {
      const qs = `t=${encodeURIComponent(token || "")}&location_code=${encodeURIComponent(code)}`
        + (start ? `&start=${encodeURIComponent(start)}` : "");
      const res = await fetch(`${API}/api/public/scheduling-link/${purpose || SLUG}/slots?${qs}`);
      const data = await res.json();
      return data && data.valid && data.self_book ? data : null;
    } catch (err) {
      return null;
    }
  }, [purpose, token]);

  const showSlots = useCallback((data) => {
    setBook(data);
    const first = (data.days || []).find((d) => d.times.length > 0);
    setBookDay(first ? first.date : "");
    setBookHalf("all");
  }, []);

  const offerBooking = useCallback(async (code) => {
    const data = code ? await loadSlots(code) : null;
    if (data) {
      setPageStarts([]);
      showSlots(data);
      setState("book");
    } else {
      setWindowsOnly(true);
      setState("flow");
    }
    setProbeDone(true);
  }, [loadSlots, showSlots]);

  useEffect(() => {
    if (state !== "flow" || probeDone || windowsOnly || !clinicsTried) return;
    offerBooking(chosen);
  }, [state, probeDone, windowsOnly, clinicsTried, chosen, offerBooking]);

  const pickBookClinic = async (code) => {
    setChosen(code);
    setBookError("");
    setState("bookChecking");
    await offerBooking(code);
  };

  const laterDates = async () => {
    if (!book || !book.later_start) return;
    const data = await loadSlots(chosen, book.later_start);
    if (data) {
      setPageStarts((prev) => [...prev, book.from]);
      showSlots(data);
    }
  };

  const earlierDates = async () => {
    const prev = pageStarts[pageStarts.length - 1];
    if (!prev) return;
    const data = await loadSlots(chosen, prev);
    if (data) {
      setPageStarts((list) => list.slice(0, -1));
      showSlots(data);
    }
  };

  const chooseWindowsInstead = () => {
    setWindowsOnly(true);
    setState("flow");
  };

  const confirmBooking = async () => {
    if (!bookPick) return;
    setBooking(true);
    setBookError("");
    try {
      const res = await fetch(
        `${API}/api/public/scheduling-link/${purpose || SLUG}/book?t=${encodeURIComponent(token || "")}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ location_code: chosen, start: bookPick.start }),
        },
      );
      const data = await res.json();
      if (data && data.booked) {
        const clinic = clinics.find((c) => c.location_code === chosen);
        setBooked({ ...bookPick, clinic: clinic ? clinic.name : "" });
        setState("booked");
        return;
      }
      // The page shows its OWN words, never the server's English body:
      // the reason code maps through the same table as every other route.
      const msg = messageForReason(L, (data && data.reason) || "not_booked", { phone: PHONE });
      setBookError(msg);
      setStatus(msg);
      const fresh = await loadSlots(chosen, book ? book.from : undefined);
      if (fresh) showSlots(fresh);
      setState("book");
    } catch (err) {
      const msg = T("book.failed", { phone: PHONE });
      setBookError(msg);
      setStatus(msg);
      setState("book");
    } finally {
      setBooking(false);
    }
  };

  const switcher = lang && state !== "language" ? (
    <div className="flex items-center gap-2" role="group"
         aria-label={T("header.switchLabel")}
         data-testid="patient-language-switcher">
      {["en", "es"].map((code) => {
        const on = lang === code;
        return (
          <button
            key={code}
            type="button"
            aria-pressed={on}
            onClick={() => applyLanguage(code)}
            data-testid={`patient-language-toggle-${code}`}
            className="min-h-[48px] px-4 rounded-full border-2"
            style={on ? S.langOn : S.langOff}
          >
            {tr(code, code === "en" ? "header.english" : "header.spanish")}
          </button>
        );
      })}
    </div>
  ) : null;

  const shell = (children) => (
    <div className="min-h-screen" data-patient-page lang={L}
         style={{ fontFamily: FONT, background: C.page, color: C.text }}>
      <style>{FOCUS_CSS}</style>
      <header className="px-5 pt-7 pb-5 flex items-center justify-between gap-4"
              style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        {/* Explicit width/height — no layout shift while the image decodes */}
        <img src={weststarLogo} width={168} height={56}
             alt="West Star Physical Therapy"
             data-testid="patient-weststar-logo"
             style={{ width: 168, height: "auto", display: "block" }} />
        {switcher}
      </header>
      <main className="px-5 pb-28 max-w-xl mx-auto">{children}</main>
      <footer className="px-5 pt-8 pb-10 flex justify-center" data-testid="patient-manna-footer">
        <img src={poweredByManna} width={132} height={12}
             alt="Powered by Manna AI Solutions"
             style={{ width: 132, height: "auto", opacity: 0.75 }} />
      </footer>
      <div aria-live="polite" role="status" className="sr-only" data-testid="patient-live-status">
        {status}
      </div>
    </div>
  );

  const chosenClinic = clinics.find((c) => c.location_code === chosen) || null;

  if (state === "loading") {
    return shell(
      <p className="mt-10 text-base" style={S.body} data-testid="patient-loading">
        {T("loading.text")}
      </p>,
    );
  }

  // THE LANGUAGE STEP — the first thing a patient does. Two large targets,
  // the browser's locale may pre-highlight, and nothing auto-skips:
  // explicit capture is the whole point of the screen.
  if (state === "language") {
    const hint = browserPreference();
    return shell(
      <div className="mt-8" data-testid="patient-language-step">
        <h1 ref={headingRef} tabIndex={-1} style={S.h1}
            className="text-4xl sm:text-5xl font-bold tracking-tight">
          {T("language.title")}
        </h1>
        <p className="mt-5 text-base" style={S.body}>{T("language.body")}</p>
        <div className="mt-8 space-y-4">
          {[["en", "language.english"], ["es", "language.spanish"]].map(([code, key]) => (
            <button
              key={code}
              type="button"
              lang={code}
              onClick={() => applyLanguage(code, { advance: true })}
              data-testid={`patient-language-choose-${code}`}
              className="w-full min-h-[48px] h-16 rounded-2xl px-5 text-left text-lg font-semibold"
              style={{
                ...S.card,
                borderWidth: hint === code ? 2 : 1,
                borderColor: hint === code ? C.accent : C.border,
                background: C.surface,
                fontSize: 20,
              }}
            >
              {tr(code, key)}
            </button>
          ))}
        </div>
      </div>,
    );
  }

  // ONE fail-closed page for every failure kind — expired, already used,
  // never existed. The API gives no oracle and neither does this. It is
  // BILINGUAL when the patient never chose (a re-tap of a consumed link
  // arrives with no session context, exactly like the text itself) and
  // in-language when they did.
  if (state === "unavailable") {
    const note = lang && failNote
      ? messageForReason(lang, failNote, { phone: PHONE })
      : "";
    const block = (code) => (
      <div key={code} lang={code} className="mt-10"
           data-testid={`patient-link-unavailable-${code}`}>
        <h1 {...(code === L ? { ref: headingRef, tabIndex: -1 } : {})} style={S.h1}
            className="text-4xl sm:text-5xl font-bold tracking-tight">
          {tr(code, "unavailable.title")}
        </h1>
        <p className="mt-5 text-base" style={S.body}>{tr(code, "unavailable.body")}</p>
      </div>
    );
    return shell(
      <div data-testid="patient-link-unavailable">
        {(lang ? [lang] : ["en", "es"]).map(block)}
        {note && (
          <p className="mt-5 text-base" style={S.helper}
             data-testid="patient-link-unavailable-note">{note}</p>
        )}
        <a href={`tel:${PHONE.replace(/-/g, "")}`}
           data-testid="patient-call-link"
           className="mt-8 inline-flex items-center gap-2 h-14 px-7 rounded-full text-base font-semibold"
           style={{ ...S.primary, borderRadius: 999, fontSize: 20 }}>
          <Phone size={18} /> {PHONE}
        </a>
      </div>,
    );
  }

  if (state === "done") {
    return shell(
      <div className="mt-10" data-testid="patient-received">
        <div className="w-14 h-14 rounded-full text-white flex items-center justify-center"
             style={{ background: C.accent }}>
          <Check size={28} />
        </div>
        <h1 ref={headingRef} tabIndex={-1} style={S.h1}
            className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight">
          {T("done.title")}
        </h1>
        <p className="mt-5 text-base" style={S.body}>{T("done.body")}</p>
        <p className="mt-5 text-base" style={S.body} data-testid="patient-done-change-note">
          {T("done.changeNote")}
        </p>
        <p className="mt-6" style={S.helper}>{T("done.questions", { phone: PHONE })}</p>
      </div>,
    );
  }

  // ── Wave 3 (2026-10-06): the self-booking screens ─────────────────────────
  if ((state === "flow" && !probeDone && !windowsOnly) || state === "bookChecking") {
    return shell(
      <p className="mt-10 text-base" style={S.body} data-testid="patient-book-checking">
        {T("book.checking")}
      </p>,
    );
  }

  if (state === "book" && book) {
    const day = (book.days || []).find((d) => d.date === bookDay) || null;
    const times = day ? day.times.filter((t) => {
      const hour = Number(t.start.slice(11, 13));
      return bookHalf === "all" || (bookHalf === "am" ? hour < 12 : hour >= 12);
    }) : [];
    const halves = [["all", "book.filterAll"], ["am", "book.filterAm"], ["pm", "book.filterPm"]];
    return shell(
      <div className="mt-8" data-testid="patient-book">
        <h1 ref={headingRef} tabIndex={-1} style={S.h1}
            className="text-4xl sm:text-5xl font-bold tracking-tight">
          {T("book.title")}
        </h1>
        <p className="mt-5 text-base" style={S.body}>
          {T("book.intro", { clinic: chosenClinic ? chosenClinic.name : T("flow.clinicFallback") })}
        </p>
        {chosenClinic && (
          <div className="mt-6 rounded-2xl px-5 py-4" style={S.card} data-testid="patient-book-clinic">
            <span className="flex items-start gap-3">
              <MapPin size={18} className="mt-1 shrink-0" style={{ color: C.accent }} />
              <span className="flex-1">
                {/* clinic NAME and CITY are roster values — never translated */}
                <span className="block" style={S.dayHeading}>{chosenClinic.name}</span>
                <span className="block mt-1" style={S.dayDate}>
                  {[chosenClinic.city, chosenClinic.distance].filter(Boolean).join(" · ")}
                </span>
              </span>
            </span>
            {clinics.length > 1 && (
              <button type="button" onClick={() => setState("bookClinic")}
                      data-testid="patient-book-change-clinic"
                      className="mt-3 text-sm font-semibold underline underline-offset-4"
                      style={{ color: C.accent }}>
                {T("book.changeClinic")}
              </button>
            )}
          </div>
        )}
        {bookError && (
          <p role="alert" className="mt-6 rounded-xl p-4" data-testid="patient-book-error"
             style={{ ...S.errorText, background: C.panel, border: `1px solid ${C.border}` }}>
            {bookError}
          </p>
        )}
        {(book.days || []).length === 0 ? (
          <p className="mt-7 text-base" style={S.body} data-testid="patient-book-none">
            {T("book.noTimes")}
          </p>
        ) : (
          <>
            <div className="mt-7 -mx-5 px-5 flex gap-3 overflow-x-auto pb-2" role="group"
                 aria-label={T("book.title")} data-testid="patient-book-days">
              {book.days.map((d) => {
                const on = d.date === bookDay;
                const dt = new Date(`${d.date}T12:00:00`);
                return (
                  <button key={d.date} type="button" aria-pressed={on}
                          onClick={() => {
                            setBookDay(d.date);
                            setBookHalf("all");
                            setStatus(T("book.dayChosen", { day: fmtFullDate(L, dt), count: d.times.length }));
                          }}
                          data-testid={`patient-book-day-${d.date}`}
                          className="shrink-0 min-w-[104px] min-h-[48px] px-4 py-3 rounded-2xl border-2 text-left"
                          style={on ? S.chipOn : S.chipOff}>
                    <span className="block text-base font-bold" style={S.dayHeading}>{fmtWeekday(L, dt)}</span>
                    <span className="block" style={S.dayDate}>{fmtMonthDay(L, dt)}</span>
                    <span className="block mt-1" style={{ ...S.helper, fontSize: 13 }}>
                      {T("book.dayCount", { count: d.times.length })}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-5 flex flex-wrap gap-2" role="group" data-testid="patient-book-halves">
              {halves.map(([key, label]) => (
                <button key={key} type="button" aria-pressed={bookHalf === key}
                        onClick={() => setBookHalf(key)}
                        data-testid={`patient-book-half-${key}`}
                        className="min-h-[48px] px-4 rounded-full border-2 text-base font-semibold"
                        style={bookHalf === key ? S.chipOn : S.chipOff}>
                  {T(label)}
                </button>
              ))}
            </div>
            {times.length === 0 ? (
              <p className="mt-5 text-base" style={S.helper} data-testid="patient-book-none-day">
                {T("book.noTimesDay")}
              </p>
            ) : (
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="patient-book-times">
                {times.map((t) => (
                  <button key={t.start} type="button"
                          onClick={() => {
                            setBookPick({ date: bookDay, start: t.start });
                            setBookError("");
                            setState("bookConfirm");
                          }}
                          data-testid={`patient-book-time-${t.start}`}
                          className="min-h-[56px] rounded-2xl border-2 text-lg font-semibold"
                          style={S.chipOff}>
                    {fmtClock(L, t.start.slice(11, 16))}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        <div className="mt-6 flex flex-wrap gap-3">
          {pageStarts.length > 0 && (
            <button type="button" onClick={earlierDates} data-testid="patient-book-earlier"
                    className="inline-flex items-center gap-2 px-4 min-h-[48px] rounded-xl"
                    style={S.secondary}>
              <ChevronLeft size={16} /> {T("book.earlierDates")}
            </button>
          )}
          {book.later_start && (
            <button type="button" onClick={laterDates} data-testid="patient-book-later"
                    className="inline-flex items-center gap-2 px-4 min-h-[48px] rounded-xl"
                    style={S.secondary}>
              {T("book.laterDates")} <ChevronRight size={16} />
            </button>
          )}
        </div>
        <div className="mt-10 rounded-xl p-4"
             style={{ background: C.panel, border: `1px solid ${C.border}` }}>
          <button type="button" onClick={chooseWindowsInstead} data-testid="patient-book-none-work"
                  className="text-base font-semibold underline underline-offset-4"
                  style={{ color: C.accent }}>
            {T("book.noneWork")}
          </button>
          <p className="mt-2" style={S.helper}>{T("book.noneWorkHint")}</p>
        </div>
      </div>,
    );
  }

  if (state === "bookClinic") {
    return shell(
      <div className="mt-8" data-testid="patient-book-clinic-step">
        <h1 ref={headingRef} tabIndex={-1} style={S.h1}
            className="text-4xl sm:text-5xl font-bold tracking-tight">
          {T("clinic.title")}
        </h1>
        <div className="mt-7 space-y-3">
          {clinics.map((c) => (
            <button key={c.location_code} type="button" aria-pressed={c.location_code === chosen}
                    onClick={() => pickBookClinic(c.location_code)}
                    data-testid={`patient-book-clinic-option-${c.location_code}`}
                    className="w-full min-h-[48px] rounded-2xl px-5 py-4 text-left"
                    style={{ ...S.card, borderColor: c.location_code === chosen ? C.accent : C.border,
                             borderWidth: c.location_code === chosen ? 2 : 1 }}>
              <span className="block" style={S.dayHeading}>{c.name}</span>
              <span className="block mt-1" style={S.dayDate}>
                {[c.city, c.distance].filter(Boolean).join(" · ")}
              </span>
            </button>
          ))}
        </div>
      </div>,
    );
  }

  if (state === "bookConfirm" && bookPick) {
    const dt = new Date(`${bookPick.date}T12:00:00`);
    return shell(
      <div className="mt-8" data-testid="patient-book-confirm">
        <h1 ref={headingRef} tabIndex={-1} style={S.h1}
            className="text-4xl sm:text-5xl font-bold tracking-tight">
          {T("book.confirmTitle")}
        </h1>
        <div className="mt-7 rounded-2xl px-5 py-4" style={S.card} data-testid="patient-book-confirm-card">
          <p style={S.dayHeading}>{fmtFullDate(L, dt)}</p>
          <p className="mt-1 text-2xl font-bold" style={S.dayHeading}>
            {fmtClock(L, bookPick.start.slice(11, 16))} <span style={S.dayDate}>{T("common.timezone")}</span>
          </p>
          {chosenClinic && (
            <p className="mt-3" style={S.dayDate}>
              {[chosenClinic.name, chosenClinic.city].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
        <button onClick={confirmBooking} disabled={booking}
                data-testid="patient-book-confirm-btn"
                className="mt-7 w-full h-14 rounded-full text-base font-semibold disabled:opacity-60"
                style={S.primary}>
          {booking ? T("book.booking") : T("book.confirmBtn")}
        </button>
        <button onClick={() => setState("book")} disabled={booking}
                data-testid="patient-book-other-time"
                className="mt-5 text-base font-semibold underline underline-offset-4"
                style={{ color: C.accent }}>
          {T("book.otherTime")}
        </button>
      </div>,
    );
  }

  if (state === "booked" && booked) {
    const dt = new Date(`${booked.date}T12:00:00`);
    return shell(
      <div className="mt-10" data-testid="patient-booked">
        <div className="w-14 h-14 rounded-full text-white flex items-center justify-center"
             style={{ background: C.accent }}>
          <Check size={28} />
        </div>
        <h1 ref={headingRef} tabIndex={-1} style={S.h1}
            className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight">
          {T("book.bookedTitle")}
        </h1>
        <div className="mt-7 rounded-2xl px-5 py-4" style={S.card} data-testid="patient-booked-summary">
          <p style={S.dayHeading}>{fmtFullDate(L, dt)}</p>
          <p className="mt-1 text-2xl font-bold" style={S.dayHeading}>
            {fmtClock(L, booked.start.slice(11, 16))} <span style={S.dayDate}>{T("common.timezone")}</span>
          </p>
          {booked.clinic && <p className="mt-3" style={S.dayDate}>{booked.clinic}</p>}
        </div>
        <p className="mt-6 text-base" style={S.body}>{T("book.bookedBody")}</p>
        <p className="mt-5" style={S.helper}>{T("book.bookedChange", { phone: PHONE })}</p>
      </div>,
    );
  }

  if (state === "clinic") {
    return shell(
      <div className="mt-8" data-testid="patient-clinic-step">
        <h1 ref={headingRef} tabIndex={-1} style={S.h1}
            className="text-4xl sm:text-5xl font-bold tracking-tight">
          {T("clinic.title")}
        </h1>
        <p className="mt-5 text-base" style={S.body}>{T("clinic.body")}</p>
        <div className="mt-7 space-y-3">
          {clinics.map((c) => {
            const on = c.location_code === chosen;
            return (
              <button
                key={c.location_code}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setChosen(c.location_code);
                  setStatus(T("status.clinicSelected", { clinic: c.name }));
                }}
                data-testid={`patient-clinic-option-${c.location_code}`}
                className="w-full min-h-[48px] rounded-2xl px-5 py-4 text-left transition-colors"
                style={{
                  ...S.card,
                  borderColor: on ? C.accent : C.border,
                  borderWidth: on ? 2 : 1,
                  background: on ? C.accentSoft : C.surface,
                }}
              >
                <span className="flex items-start gap-3">
                  <MapPin size={18} className="mt-1 shrink-0"
                          style={{ color: on ? C.accent : C.muted }} />
                  <span className="flex-1">
                    {/* clinic NAME and CITY are roster values — never translated */}
                    <span className="block" style={{ ...S.dayHeading, fontWeight: on ? 700 : 600 }}>
                      {c.name}
                    </span>
                    <span className="block mt-1" style={S.dayDate}>
                      {[c.city, c.distance].filter(Boolean).join(" · ")}
                    </span>
                    {c.is_assigned && (
                      <span className="inline-block mt-2" style={S.pill}
                            data-testid={`patient-clinic-yours-${c.location_code}`}>
                        {T("clinic.yours")}
                      </span>
                    )}
                  </span>
                  {/* selection is never colour alone */}
                  {on && <Check size={20} className="mt-1" style={{ color: C.accent }} />}
                </span>
              </button>
            );
          })}
        </div>
        <button onClick={() => setState("flow")}
                data-testid="patient-clinic-back-btn"
                className="mt-6 text-base font-semibold underline underline-offset-4"
                style={{ color: C.accent }}>
          {T("clinic.changeTimes")}
        </button>
        <button onClick={() => setState("review")} disabled={!chosen}
                data-testid="patient-clinic-continue-btn"
                className="mt-7 w-full h-14 rounded-full text-base font-semibold disabled:opacity-60"
                style={S.primary}>
          {T("common.continue")}
        </button>
      </div>,
    );
  }

  if (state === "review") {
    return shell(
      <div className="mt-8" data-testid="patient-review">
        <h1 ref={headingRef} tabIndex={-1} style={S.h1}
            className="text-4xl sm:text-5xl font-bold tracking-tight">
          {T("review.title")}
        </h1>
        {chosenClinic && (
          <div className="mt-7 rounded-2xl px-5 py-4" style={S.card}
               data-testid="patient-review-clinic">
            <p style={{ ...S.helper, fontSize: 13, textTransform: "uppercase", letterSpacing: "1px" }}>
              {T("review.locationLabel")}
            </p>
            <p className="mt-1" style={S.dayHeading}>{chosenClinic.name}</p>
            <p className="mt-1" style={S.dayDate}>
              {[chosenClinic.city, chosenClinic.distance].filter(Boolean).join(" · ")}
            </p>
            <button onClick={() => setState("clinic")}
                    data-testid="patient-review-edit-clinic-btn"
                    className="mt-3 text-sm font-semibold underline underline-offset-4"
                    style={{ color: C.accent }}>
              {T("review.changeLocation")}
            </button>
          </div>
        )}
        <ul className="mt-7 space-y-3">
          {picked.map((p) => (
            <li key={`${p.date}-${p.start}`}
                data-testid={`patient-review-row-${p.date}-${p.start}`}
                className="rounded-2xl px-5 py-4" style={S.card}>
              <p style={S.dayHeading}>
                {fmtFullDate(L, new Date(`${p.date}T12:00:00`))}
              </p>
              <p className="mt-1" style={S.dayDate}>
                {fmtClockRange(L, p.start, p.end)} {T("common.timezone")}
              </p>
            </li>
          ))}
        </ul>
        <button onClick={() => setState("flow")}
                data-testid="patient-edit-btn"
                className="mt-6 text-base font-semibold underline underline-offset-4"
                style={{ color: C.accent }}>
          {T("review.changeTimes")}
        </button>
        {showTelehealth && (
          <div className="mt-8 rounded-xl p-4" style={S.card}
               data-testid="patient-telehealth-box">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={telehealthOk}
                     onChange={(e) => setTelehealthOk(e.target.checked)}
                     data-testid="patient-telehealth-checkbox"
                     className="mt-1 h-5 w-5 shrink-0"
                     style={{ accentColor: C.accent }} />
              <span className="text-base" style={S.body}>{T("telehealth.label")}</span>
            </label>
            <p className="mt-2 ml-8" style={S.helper}>{T("telehealth.note")}</p>
          </div>
        )}
        <p className="mt-8 rounded-xl p-4"
           style={{ ...S.helper, background: C.panel, border: `1px solid ${C.border}` }}
           data-testid="patient-not-booking-note">
          {T("review.notBookingNote")}
        </p>
        <button onClick={send} disabled={sending}
                data-testid="patient-send-btn"
                className="mt-7 w-full h-14 rounded-full text-base font-semibold disabled:opacity-60"
                style={S.primary}>
          {sending ? T("review.sending") : T("review.send")}
        </button>
      </div>,
    );
  }

  return shell(
    <div className="mt-8" data-testid="patient-availability-flow">
      <h1 ref={headingRef} tabIndex={-1} style={S.h1}
          className="text-4xl sm:text-5xl font-bold tracking-tight">
        {info?.first_name
          ? T("flow.titleNamed", { name: info.first_name })
          : T("flow.title")}
      </h1>
      <p className="mt-5 text-base" style={S.body}>
        {T("flow.intro", {
          clinic: info?.clinic_name || T("flow.clinicFallback"),
          max: MAX_WINDOWS,
          timezone: T("common.timezone"),
        })}
      </p>

      <div className="mt-8 space-y-4">
        {days.map((d) => {
          const date = ymd(d);
          return (
            <section key={date} data-testid={`patient-day-${date}`}
                     className="rounded-2xl p-5" style={S.card}>
              <div className="flex items-baseline gap-3">
                <span className="text-lg md:text-lg font-bold" style={S.dayHeading}>
                  {fmtWeekday(L, d)}
                </span>
                <span style={S.dayDate}>{fmtMonthDay(L, d)}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                {CHIPS.map((chip) => {
                  const on = isPicked(date, chip.start, chip.end);
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(date, chip)}
                      data-testid={`patient-chip-${date}-${chip.key}`}
                      className="min-h-[48px] px-4 py-2 rounded-2xl border-2 text-left transition-colors"
                      style={on ? S.chipOn : S.chipOff}
                    >
                      <span className="flex items-center gap-2 text-base font-semibold">
                        {/* selection is never colour alone */}
                        {on && <Check size={16} style={{ color: C.accent }} />}
                        {T(`chips.${chip.key}`)}
                      </span>
                      <span className="block" style={{ ...S.helper, fontSize: 13 }}>
                        {T(`chips.${chip.key}Hint`)}
                      </span>
                    </button>
                  );
                })}
              </div>

              {exactFor === date ? (
                <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${C.border}` }}>
                  <div className="flex flex-wrap items-end gap-3">
                    <label style={S.helper}>
                      {T("flow.from")}
                      <select value={exact.from}
                              onChange={(e) => setExact((p) => ({ ...p, from: e.target.value }))}
                              data-testid={`patient-exact-from-${date}`}
                              className="mt-1 block min-h-[48px] rounded-xl border-2 px-3"
                              style={{ borderColor: C.border }}>
                        {STEP_TIMES.map((hhmm) => (
                          <option key={hhmm} value={hhmm}>{fmtClock(L, hhmm)}</option>
                        ))}
                      </select>
                    </label>
                    <label style={S.helper}>
                      {T("flow.until")}
                      <select value={exact.until}
                              onChange={(e) => setExact((p) => ({ ...p, until: e.target.value }))}
                              data-testid={`patient-exact-until-${date}`}
                              className="mt-1 block min-h-[48px] rounded-xl border-2 px-3"
                              style={{ borderColor: C.border }}>
                        {STEP_TIMES.map((hhmm) => (
                          <option key={hhmm} value={hhmm}>{fmtClock(L, hhmm)}</option>
                        ))}
                      </select>
                    </label>
                    <button type="button" onClick={() => addExact(date)}
                            data-testid={`patient-exact-add-${date}`}
                            className="min-h-[48px] px-5 rounded-full text-sm font-semibold"
                            style={{ ...S.primary, minHeight: 48, borderRadius: 999, fontSize: 15 }}>
                      {T("flow.exactAdd")}
                    </button>
                  </div>
                  {exactError && (
                    <p role="alert" data-testid={`patient-exact-error-${date}`}
                       style={S.errorText} className="mt-3">{exactError}</p>
                  )}
                </div>
              ) : (
                <button type="button"
                        onClick={() => { setExactFor(date); setExactError(""); }}
                        data-testid={`patient-exact-open-${date}`}
                        className="mt-4 text-sm font-semibold underline underline-offset-4"
                        style={{ color: C.accent }}>
                  {T("flow.exactOpen")}
                </button>
              )}
            </section>
          );
        })}
      </div>

      {canPageMore && (
        <button type="button" onClick={() => setPageOffset((p) => p + PAGE_DAYS)}
                data-testid="patient-later-dates-btn"
                className="mt-6 inline-flex items-center gap-2 px-4 min-h-[48px] rounded-xl"
                style={S.secondary}>
          {T("flow.laterDates")} <ChevronRight size={16} />
        </button>
      )}

      <div className="fixed bottom-0 left-0 right-0 px-5 py-4"
           style={{ background: C.surface, borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-xl mx-auto flex items-center gap-4">
          <span style={S.pill} data-testid="patient-counter">
            {T("flow.counter", { count: picked.length, max: MAX_WINDOWS })}
          </span>
          <button type="button" disabled={picked.length < 1}
                  onClick={() => setState("clinic")}
                  data-testid="patient-continue-btn"
                  className="ml-auto min-h-[48px] px-6 rounded-full text-base font-semibold disabled:opacity-50"
                  style={{ ...S.primary, borderRadius: 999 }}>
            {T("common.continue")}
          </button>
        </div>
      </div>
    </div>,
  );
}
